import { post } from 'axios';
import { getFormattedPrice } from 'Components/ContractGenerator/shared/helpers/priceHelpers';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { contractGeneratorSelectors } from 'Redux/ducks/contractGenerator';
import Badge from 'ViatoUi/Shared/DataDisplay/Badge';
import { FlexBox } from 'ViatoUi/Shared/Layouts/Flex';
import Spacer from 'ViatoUi/Shared/Layouts/Spacer';
import Loader from 'ViatoUi/Shared/Loader';
import Paper from 'ViatoUi/Shared/Surfaces/Paper';
import { Anchor, Headline, Paragraph } from 'ViatoUi/Shared/Typo';
import Translation from 'ViatoUi/Shared/Utilities/Translation';
import Button from 'ViatoUi/Web/FormElements/Buttons/Button';

import styles from './Product.module.scss';

const Product = ({
	translationKey,
	onSelectProduct,
	onSelectOption,
	options,
	selectedOption,
	productDiscountValue,
	productDiscountType,
	feeDiscountValue,
	feeDiscountType,
	increaseWidth,
	highlight,
	isSinglePayment,
	isForSale,
	description,
	productOption,
	link
}) => {
	const { t, i18n } = useTranslation(['contractGenerator', 'translation']);
	const urlPrefix = process.env.NODE_ENV !== 'production' ? '' : 'https://contract-generator.viato.travel';

	const { selectedRooms, selectedPaymentType, selectedContractTermInMonths } = useSelector(contractGeneratorSelectors.getDataToSubmit);

	const [productPrice, setProductPrice] = useState(null);
	const [productPriceDiscounted, setProductPriceDiscounted] = useState(null);
	const [feePrice, setFeePrice] = useState(null);
	const [feePriceDiscounted, setFeePriceDiscounted] = useState(null);

	useEffect(() => {
		setProductPrice(null);
		post(`${urlPrefix}/api/prices/priceForProduct`, {
			priceSettings: productOption,
			selectedRooms,
			selectedPaymentType,
			selectedContractTermInMonths,
			settings: { useDiscount: false, isSetupFee: false }
		}).then(response => setProductPrice(response.data.price));

		setProductPriceDiscounted(null);
		post(`${urlPrefix}/api/prices/priceForProduct`, {
			priceSettings: productOption,
			selectedRooms,
			selectedPaymentType,
			selectedContractTermInMonths,
			settings: { useDiscount: true, isSetupFee: false }
		}).then(response => setProductPriceDiscounted(response.data.price));

		setFeePrice(null);
		post(`${urlPrefix}/api/prices/priceForProduct`, {
			priceSettings: productOption.setupFee,
			selectedRooms,
			selectedPaymentType,
			selectedContractTermInMonths,
			settings: { useDiscount: false, isSetupFee: true }
		}).then(response => setFeePrice(response.data.price));

		setFeePriceDiscounted(null);
		post(`${urlPrefix}/api/prices/priceForProduct`, {
			priceSettings: productOption.setupFee,
			selectedRooms,
			selectedPaymentType,
			selectedContractTermInMonths,
			settings: { useDiscount: true, isSetupFee: true }
		}).then(response => setFeePriceDiscounted(response.data.price));
	}, [productOption, selectedRooms, selectedPaymentType, selectedContractTermInMonths]);

	let productTitle = t(`translation:products.${translationKey}.title`);
	if (link.length > 0) {
		productTitle = (
			<Anchor href={`${link}?lang=${i18n.language}`} target='_blank'>
				{productTitle}
			</Anchor>
		);
	}

	const getContent = () => {
		if (options.length > 1) {
			return (
				<>
					<Paragraph font='large'>{t('pages.step1.products.product.choseOptions', { amount: options.length })}</Paragraph>
					<FlexBox alignItems='center' flexDirection='column'>
						{options.map((option, index) => (
							<FlexBox key={option.minPrice} alignItems='center'>
								<FlexBox
									alignItems='center'
									className={styles.fakeRadio}
									data={{ 'data-checked': index === parseInt(selectedOption) }}
									justifyContent='center'
									onClick={() => onSelectOption(index)}
								>
									<div className={styles.dot} />
								</FlexBox>
								<Spacer width='s' />
								<Translation
									components={description.components || []}
									translationKey={`translation:products.${translationKey}.options.${index}.description`}
									translationNamespace='contractGenerator'
								/>
							</FlexBox>
						))}
					</FlexBox>
				</>
			);
		}

		return (
			<Paragraph font='large' isDiv>
				<Translation
					components={description.components || []}
					translationKey={`translation:products.${translationKey}.options.0.description`}
					translationNamespace='contractGenerator'
				/>
			</Paragraph>
		);
	};

	const getBadgeText = (discountType, discountValue, defaultPrice) => {
		if (discountType === 'percentage') {
			if (discountValue > 100) {
				return 100;
			}

			return discountValue;
		}

		if (discountValue > defaultPrice * 100) {
			return defaultPrice;
		}

		return discountValue / 100;
	};

	const getPriceContent = (defaultPrice, discountedPrice, discountValue, discountType) => {
		if (defaultPrice === null || discountedPrice === null) {
			return <Loader iconOnly />;
		}

		if (discountValue > 0) {
			return (
				<>
					<span className={styles.oldPrice}>{getFormattedPrice(defaultPrice)}&nbsp;€*</span> {getFormattedPrice(discountedPrice)}
					&nbsp;€*{' '}
					<Badge
						label={
							discountType === 'percentage'
								? `-${getBadgeText(discountType, discountValue, defaultPrice)}%`
								: `-${getFormattedPrice(getBadgeText(discountType, discountValue, defaultPrice))}€*`
						}
						size='s'
						type='error'
						inline
					/>
				</>
			);
		}

		return <>{getFormattedPrice(defaultPrice)}&nbsp;€*</>;
	};

	return (
		<Paper
			className={styles.product}
			data={{
				'data-increase-width': increaseWidth,
				'data-highlight': highlight,
				'data-highlight-text': highlight ? t(`translation:products.${translationKey}.highlightText`) : ''
			}}
			elevation='strong'
			outline={highlight}
			roundness='strong'
			spacing='l'
		>
			<div>
				<Headline type={increaseWidth ? 'h3' : 'h5'}>{productTitle}</Headline>
				<Spacer height='m' />
				{getContent()}
				<Spacer height='l' />
			</div>
			<div>
				<Headline type='h3'>
					<span className={styles.from}>
						{isForSale ? t(`pages.step1.products.product.from`) : t(`pages.step1.products.product.onRequest`)}
					</span>{' '}
					{isForSale && getPriceContent(productPrice, productPriceDiscounted, productDiscountValue, productDiscountType)}
				</Headline>
				{!isSinglePayment && <Paragraph font='large'>{t(`pages.step1.products.product.monthly`)}</Paragraph>}
				<Spacer height='l' />
				<Button className={styles.button} type='dark' onClick={() => onSelectProduct()}>
					{isForSale ? t(`app.userActions.choose`) : t(`app.userActions.inquire`)}
				</Button>
				{feePrice > 0 ? (
					<>
						<Spacer height='m' />
						<Paragraph className={styles.fee} color='extraDarkSmoke' font='tiny' isDiv>
							{t(`pages.step1.products.product.setupFee`)}
							<br />
							{t(`pages.step1.products.product.once`)} {t(`pages.step1.products.product.from`)}{' '}
							{getPriceContent(feePrice, feePriceDiscounted, feeDiscountValue, feeDiscountType)}
						</Paragraph>
					</>
				) : (
					<div className={styles.spacer} />
				)}
			</div>
		</Paper>
	);
};

Product.propTypes = {
	productOption: PropTypes.object.isRequired,
	translationKey: PropTypes.string.isRequired,
	onSelectProduct: PropTypes.func.isRequired,
	onSelectOption: PropTypes.func.isRequired,
	options: PropTypes.array.isRequired,
	description: PropTypes.object.isRequired,
	selectedOption: PropTypes.number.isRequired,
	productDiscountValue: PropTypes.number.isRequired,
	productDiscountType: PropTypes.string.isRequired,
	feeDiscountValue: PropTypes.number.isRequired,
	feeDiscountType: PropTypes.string.isRequired,
	increaseWidth: PropTypes.bool.isRequired,
	highlight: PropTypes.bool.isRequired,
	isSinglePayment: PropTypes.bool.isRequired,
	isForSale: PropTypes.bool.isRequired,
	link: PropTypes.string.isRequired
};

export default Product;
